import {
  isRouteErrorResponse,
  NavLink,
  useRouteError,
} from "react-router"
import { useEffect } from "react"
import { Alert } from "./ui/alert"
import { Button } from "./ui/button"
import {
  captureClientError,
  captureErrorPage,
  initPosthog,
} from "~/client/posthog.client"
import { logout } from "~/client/firebase.client"

export function ErrorBoundary() {
  const error = useRouteError()
  // Initialize PostHog and capture on the client only
  useEffect(() => {
    initPosthog()
    if (error) {
      captureClientError(error, { source: "ErrorBoundary" })

      // Fire a dedicated event for dashboard filtering
      if (isRouteErrorResponse(error)) {
        switch (error.status) {
          case 401:
            captureErrorPage("no_access", 401)
            break
          case 404:
            captureErrorPage("not_found", 404)
            break
          case 429:
            captureErrorPage("rate_limited", 429)
            break
          default:
            captureErrorPage("unexpected", error.status)
        }
      } else if (
        error instanceof Error &&
        (error.message.includes("[unauthenticated]") ||
          error.message.includes("invalid auth token"))
      ) {
        captureErrorPage("no_access", 401)
      } else {
        captureErrorPage("unexpected")
      }
    }
  }, [error])
  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 401:
        return <NoAccessError />
      case 404:
        return <NotFoundError />
      case 429:
        return <TooManyRequestsError />
    }
  }

  // Check for unauthenticated error message
  if (
    error instanceof Error &&
    (error.message.includes("[unauthenticated]") ||
      error.message.includes("invalid auth token"))
  ) {
    return <NoAccessError />
  }

  return <UnexpectedError error={error} />
}

function NoAccessError() {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-background gap-6">
      <Alert
        variant="destructive"
        className="text-lg px-4 py-6 max-w-md mx-auto"
      >
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-2xl font-semibold">No access</h2>
          <p className="text-foreground">
            The current account does not have access to this content
          </p>
        </div>
      </Alert>
      <Button asChild>
        <NavLink to="/select-account">Select another account?</NavLink>
      </Button>
      <Button onClick={() => logout()} variant="link">
        Logout
      </Button>
    </div>
  )
}

function NotFoundError() {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-background gap-6">
      <Alert
        variant="destructive"
        className="text-lg px-4 py-6 max-w-md mx-auto"
      >
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-2xl font-semibold">Page Not Found</h2>
          <p className="text-foreground">
            The page you&apos;re looking for doesn&apos;t exist or has been
            moved.
          </p>
        </div>
      </Alert>
      <Button asChild>
        <NavLink to="/">Return to Home</NavLink>
      </Button>
    </div>
  )
}

function TooManyRequestsError() {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-background gap-6">
      <Alert
        variant="destructive"
        className="text-lg px-4 py-6 max-w-md mx-auto"
      >
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-2xl font-semibold">Quota exceeded</h2>
          <p className="text-foreground">
            You have made too many requests. Please contact the Pine59 team to
            increase your quota.
          </p>
        </div>
      </Alert>
      <Button asChild>
        <NavLink to="/">Return to Home</NavLink>
      </Button>
    </div>
  )
}

function UnexpectedError({ error }: { error: unknown }) {
  console.error("Unexpected error:", error)
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-background gap-6">
      <Alert
        variant="destructive"
        className="text-lg px-4 py-6 max-w-md mx-auto"
      >
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-2xl font-semibold">Unexpected error</h2>
          <p className="text-foreground">
            Try again later or contact support if the issue persists.{" "}
            <a
              className="underline text-foreground"
              href="mailto:support@pine59.com"
            >
              support@pine59.com
            </a>
          </p>
        </div>
      </Alert>
      <Button asChild>
        <NavLink to="/">Return to Home</NavLink>
      </Button>
    </div>
  )
}
